import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { invariant } from '@utils/invariant';

import { Button } from '../../../components/button/Button';
import { PageHeader } from '../../../components/PageHeader';
import { formatDateTime } from '../../../utils/date';
import { getDisplayError } from '../../../utils/get-display-error';
import { Pill } from '../../../components/pill/Pill';
import { DataField } from '../../../components/DataField';
import { RoleSelect } from '../components/RoleSelect';
import { useAuth } from '../../../contexts/auth-context';
import { CheckboxWithLabel } from '../../../components/checkbox/CheckboxWithLabel';
import { captureException } from '@sentry/react';
import { getInternalUser, updateInternalUser } from '../models';
import { ErrorState } from '../../../components/error-state/ErrorState';
import { InternalRole } from '../../auth/endpoints/MeEndpoint.types';

const UserPage = () => {
  const { me } = useAuth();
  const { userId } = useParams<{ userId: string }>();
  invariant(userId);

  const { data, error, mutate } = useSWR(`/api/v1/users/${userId}`, () => getInternalUser(userId!));

  const user = data?.user;
  // An owner's role and activation state are immutable server-side, so don't
  // offer the controls at all.
  const isOwnerAccount = user?.role === InternalRole.InternalOwner;

  async function update(body: Parameters<typeof updateInternalUser>[1]) {
    try {
      await updateInternalUser(userId!, body);
      await mutate();
    } catch (err) {
      captureException(err);
      console.error(err);
      throw err;
    }
  }

  if (!user) {
    return (
      <ErrorState
        error={error}
        title={error ? 'Gebruiker kon niet worden geladen' : 'Gebruiker niet gevonden'}
        description={error ? getDisplayError(error) : 'De gevraagde gebruiker werd niet gevonden.'}
        onRetry={error ? () => mutate() : undefined}
        variant="page"
      />
    );
  } else {
    return (
      <>
        <PageHeader title={user.name} />

        <div className="grid gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="heading-two">{user.name}</h2>
            <Pill color={user.isActive ? 'positive' : 'negative'} size="sm">
              {user.isActive ? 'Actief' : 'Inactief'}
            </Pill>

            <div className="ml-auto">
              <Button
                isDisabled={isOwnerAccount}
                onTrigger={async () => {
                  try {
                    await update({ isActive: !user.isActive });
                    toast.success(user.isActive ? 'Gebruiker gedeactiveerd' : 'Gebruiker geheractiveerd');
                  } catch (err) {
                    toast.error(
                      `Kon gebruiker niet ${user.isActive ? 'deactiveren' : 'heractiveren'}: ${getDisplayError(err)}`,
                    );
                  }
                }}
              >
                {user.isActive ? 'Deactiveer' : 'Activeer'}
              </Button>
            </div>
          </div>

          <div className="card flex gap-5 p-5">
            {user.image ? (
              <img
                src={user.image}
                alt={user.name}
                className="h-16 w-16 shrink-0 rounded-md border border-border object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-border bg-surface-sunken text-2xl font-semibold text-fg-muted">
                {user.name.trim().charAt(0).toUpperCase() || 'T'}
              </div>
            )}

            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <div className="w-52">
                <DataField title="Rol">
                  <RoleSelect
                    isDisabled={user.id === me.id || isOwnerAccount}
                    onSelect={async (newRole) => {
                      try {
                        await update({
                          role: newRole as Parameters<typeof updateInternalUser>[1]['role'],
                        });
                        toast.success('Rol gewijzigd');
                      } catch (err) {
                        toast.error(`Kon rol niet wijzigen: ${getDisplayError(err)}`);
                      }
                    }}
                    value={user.role ?? ''}
                  />
                </DataField>
              </div>

              <DataField title="Naam">{user.name}</DataField>
              <DataField title="Email">{user.email}</DataField>
              <DataField title="Aangemaakt op">{formatDateTime(user.createdAt)}</DataField>

              <div className="flex flex-col gap-2.5 pt-1">
                <CheckboxWithLabel
                  isChecked={user.shouldReceiveQuotationRequestNotifications}
                  onChange={async (newIsChecked) => {
                    try {
                      await update({ shouldReceiveQuotationRequestNotifications: newIsChecked });
                      toast.success('Notificatie voorkeuren gewijzigd');
                    } catch (err) {
                      toast.error(`Kon notificatie voorkeuren niet wijzigen: ${getDisplayError(err)}`);
                    }
                  }}
                  labelText="Offerte aanvraag notificaties ontvangen"
                />

                <CheckboxWithLabel
                  isChecked={user.shouldReceiveComplaintNotifications}
                  onChange={async (newIsChecked) => {
                    try {
                      await update({ shouldReceiveComplaintNotifications: newIsChecked });
                      toast.success('Notificatie voorkeuren gewijzigd');
                    } catch (err) {
                      toast.error(`Kon notificatie voorkeuren niet wijzigen: ${getDisplayError(err)}`);
                    }
                  }}
                  labelText="Order klachten notificaties ontvangen"
                />

                <CheckboxWithLabel
                  isChecked={user.shouldReceiveFinancialNotifications}
                  onChange={async (newIsChecked) => {
                    try {
                      await update({ shouldReceiveFinancialNotifications: newIsChecked });
                      toast.success('Notificatie voorkeuren gewijzigd');
                    } catch (err) {
                      toast.error(`Kon notificatie voorkeuren niet wijzigen: ${getDisplayError(err)}`);
                    }
                  }}
                  labelText="Financiele notificaties ontvangen"
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
};

export default UserPage;
